// Import necessary dependencies and helper functions
import { CalendarEvent, EventsObj, CalendarViewType, FilterState } from '../../helpers/Calendar-Interfaces';
import { Calendar, CalendarOptions } from '@fullcalendar/core';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';

import {
    addClassForExpireDate,
    eventRender,
    groupRowsByDate,
    showPrevious,
    modalContentHtml, toggleCalendarView, getCalendarView
} from '../../helpers/fullCalendar-helper';
import { doesEventIntersectWithMonth } from '../../helpers/Calendar-helper';
var moment = require('moment');

// Messages shown when the current view has nothing to display.
// They are all rendered as the illustrated empty state; other texts (loading, fetch errors) stay plain.
const infoMessage = {
    noEvents: 'There are no events to display.',
    noEventsThisMonth: 'There are no events scheduled for the rest of this month. Check back soon, or jump ahead to see what\'s coming up next month.',
    noEventsThisDay: 'There are no events scheduled for this day. Check back soon, or pick another day to see what\'s coming up.',
    selectCalendar: 'Select a calendar from the Filter Calendars list to see more events.'
};
const emptyStateMessages: string[] = Object.values(infoMessage);
// Only these messages promise new events, so only they get the "Check Back Soon" title.
const checkBackSoonMessages: string[] = [infoMessage.noEventsThisMonth, infoMessage.noEventsThisDay];

// Define the EventsFullCalendar class
export class EventsFullCalendar {
    // Private class properties
    private calendarEl: HTMLElement;
    private calendarTable: HTMLTableElement;
    private calendar: Calendar;
    private modalTemplate: string;
    private minDateLimit: Date | null;
    private previousEventsBtn: HTMLButtonElement;
    private viewMode: CalendarViewType;
    private events: EventsObj;
    private changeViewBtn: HTMLButtonElement;
    private referenceMonth: number;
    // Constructor for initializing the class with calendar element and minimum date
    constructor(calendarElement: HTMLElement, minDate: Date) {
        this.calendarEl = calendarElement;
        this.minDateLimit = minDate;

        // Fetch modal template script and set it as the modal template

        const modalTemplateScript = document.querySelector('#Event_template') as HTMLScriptElement;
        this.modalTemplate = modalTemplateScript.innerHTML;

        // Fetch previous events button
        this.previousEventsBtn = document.querySelector('#mNav_PreviewFilter');
        // Initialize the EventsFullCalendar instance
        this.init();
    }

    // Initialize the class
    private init() {
        // Add click event listener for the "Show Previous" button
        this.previousEventsBtn.addEventListener('click', () => {
            showPrevious(this.calendarTable, this.previousEventsBtn, this.viewMode);
            this.showNoEventsInfo();
        });

        // Initialize calendar options and create a new Calendar instance
        this.calendar = new Calendar(this.calendarEl, this.initOptions());

        this.setupCalendarViewButtons();
        this.initializeDefaultCalendarViewButton();

        const prevWeekBtn = document.getElementById('prev-week') as HTMLButtonElement;
        const nextWeekBtn = document.getElementById('next-week') as HTMLButtonElement;

        prevWeekBtn.addEventListener('click', () => this.navigateWeek(-1));
        nextWeekBtn.addEventListener('click', () => this.navigateWeek(1));
    }

    // Initialize calendar options
    private initOptions(): CalendarOptions {
        return {
            plugins: [listPlugin, dayGridPlugin],
            initialView: 'listMonth',
            progressiveEventRendering: true,
            validRange: { start: this.minDateLimit },
            eventContent: this.eventRender.bind(this),
            listDayFormat: {
                weekday: 'long'
            },
            listDaySideFormat: {
                day: '2-digit'
            },
            headerToolbar: {
                left: '',
                center: '',
                right: '',
            },
            height: 'auto',
            views: {
                listAllYears: {
                    type: 'list',
                    visibleRange: {
                        start: '1980-01-01',
                        end: '2230-01-01'
                    },
                },
                dayGridMonth: {
                    type: 'dayGridMonth'
                },
                dayGridDay: {
                    type: 'dayGridDay'
                },
                dayGridWeek: {
                    type: 'dayGridWeek'
                }
            },
            datesSet: (arg) => {
                const nav = document.getElementById('week-nav-buttons');
             

                if (arg.view.type === 'dayGridWeek') {
                    nav.style.setProperty('display', 'block');
                    this.updateArrowStates();
                } else {
                    nav.style.setProperty('display', 'none');
                }
            },
        };
    }

    private navigateWeek(direction: number) {
        const currentStart = moment(this.calendar.view.currentStart);
        const newStart = currentStart.clone().add(direction, 'week');

        const referenceMonth = this.referenceMonth;

        const weekDates = Array.from({ length: 7 }, (_, i) =>
            newStart.clone().add(i, 'days')
        );

        const overlapsReferenceMonth = weekDates.some(date =>
            date.month() === referenceMonth
        );

        if (overlapsReferenceMonth) {
            this.changeViewBtn.setAttribute('aria-week-date', newStart.format('YYYY-MM-DD'));
            this.calendar.gotoDate(newStart.toDate());
            this.updateArrowStates();
            this.showInfoMessage();
            this.renderCalendar();
        }
    }

    private updateArrowStates() {
        const prevBtn = document.getElementById('prev-week') as HTMLButtonElement;
        const nextBtn = document.getElementById('next-week') as HTMLButtonElement;

        const currentStart = moment(this.calendar.view.currentStart);

        const prevWeekLastDay = currentStart.clone().subtract(1, 'day');
        const canGoPrev = prevWeekLastDay.month() === this.referenceMonth;

        const nextWeekFirstDay = currentStart.clone().add(7, 'days');
        const canGoNext = nextWeekFirstDay.month() === this.referenceMonth;

        prevBtn.disabled = !canGoPrev;
        nextBtn.disabled = !canGoNext;
    }

    public changeToggleViewBtn(newView: CalendarViewType) {
        const weekNav = document.getElementById('week-nav-buttons');
        const activeButton = document.querySelector(`.calendar-toggle-view-btn[aria-btn-view="${newView}"]`) as HTMLButtonElement;
        this.changeViewBtn = activeButton;

        if (!activeButton) return;

        if (newView === 'dayGridWeek') {
            const today = new Date();
            const selectedDate = this.calendar.getDate();

            const isCurrentMonth =
                today.getFullYear() === selectedDate.getFullYear() &&
                today.getMonth() === selectedDate.getMonth();
            const toDate = isCurrentMonth
                ? today
                : new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);

            this.calendar.gotoDate(toDate);
            activeButton.setAttribute('aria-week-date', moment(toDate).format('YYYY-MM-DD'));

            if (weekNav) weekNav.style.display = 'block';
        } else {
            activeButton.removeAttribute('aria-week-date');
            if (weekNav) weekNav.style.display = 'none';
        }
    }

    private setupCalendarViewButtons() {
        const viewButtons = document.querySelectorAll('.calendar-toggle-view-btn') as NodeListOf<HTMLButtonElement>;

        viewButtons.forEach((button) => {
            button.addEventListener('click', () => {
                const newView = button.getAttribute('aria-btn-view') as CalendarViewType;
                if (!newView) return;

                this.calendarView(newView, this.calendar.getDate());
                this.changeToggleViewBtn(newView);

                viewButtons.forEach((btn) => {
                    btn.classList.remove('active');
                    btn.disabled = false;
                });

                button.classList.add('active');
                button.disabled = true;
            });
        });
    }
    private initializeDefaultCalendarViewButton() {
        const defaultButton = document.querySelector('.calendar-toggle-view-btn[aria-btn-view="listMonth"]') as HTMLButtonElement;

        if (defaultButton) {
            defaultButton.classList.add('active');
            defaultButton.disabled = true;
            this.changeViewBtn = defaultButton;
        }

        const weekNav = document.getElementById('week-nav-buttons');
        if (weekNav) {
            weekNav.style.display = 'none';
        }
    }

    private checkDayGridView(isHidden: boolean) {
        const wrapper = document.querySelector('.calendar-wrapper');
        wrapper.classList.toggle('calendar-hidden', isHidden);
    }

    // Calculates the end time of an event based on its Duration or defaults to +1 hour
    private getEventEndWithMoment(event, eventStart) {
        const durationStr = event.Duration || '';
        const parts = durationStr.split(/–|&ndash;/); // Support both types of dashes

        if (parts.length === 2) {
            const endTimeStr = parts[1].replace(/&nbsp;/g, '').trim(); // e.g., "07:30PM"
            const mEnd = moment(`${eventStart.format('YYYY-MM-DD')} ${endTimeStr}`, 'YYYY-MM-DD hh:mmA');

            if (mEnd.isValid()) return mEnd;
        }

        // Fallback: +1 hour
        return eventStart.clone().add(1, 'hours');
    }

    // Event rendering function for customizing the appearance of events
    private eventRender(info: any): { domNodes?: Node[] } {
        let eventContainer;
        // If the view is 'dayGridMonth', return default rendering
        if (info.view.type === 'dayGridMonth' || info.view.type === 'dayGridDay' || info.view.type === 'dayGridWeek') {
            const addModalAttrs = (element) => {
                element.setAttribute('data-a11y-dialog-show', 'Event_modal');
                element.setAttribute('data-event-id', `${info.event.extendedProps.CalendarEventsID}-${moment(info.event.extendedProps.eventStartDate).format("YYYY-MM-DD")}`);
                return element
            }
            // Return title as default rendering, but ensure click works
            eventContainer = document.createElement('div');
            const dotDiv = document.createElement('div');
            dotDiv.classList.add('fc-daygrid-event-dot');
            addModalAttrs(dotDiv);
            /*const time = document.createElement('div');
            time.classList.add('fc-event-time');
            time.innerText = `${moment(info.event.extendedProps['eventStartDate']).format("h:mma").replace("m", "")} - ${moment(info.event.extendedProps["eventEndDate"]).format("h:mma").replace("m", "")}`;
            addModalAttrs(time);*/
            const title = document.createElement('div');
            title.classList.add('fc-event-title');
            addModalAttrs(title);
            title.innerText = info.event.title
            const modalContent = modalContentHtml(info.event);
            eventContainer.appendChild(dotDiv);
           // console.log('time: ', time);
           // eventContainer.appendChild(time);
            eventContainer.appendChild(title);
            eventContainer.innerHTML += modalContent;
        } else {
            eventContainer = document.createElement('div');

            // Add class for expired date to the event container
            addClassForExpireDate(eventContainer, info.event.extendedProps.eventEndDate);
            eventContainer.classList.add('calendar-event-row');
            eventContainer.setAttribute('calendar-event-id', info.event.extendedProps.CalendarEventsID);

            // Render the event using the provided helper function
            const event = eventRender(info.event.title, info.event.extendedProps, this.modalTemplate);
            eventContainer.innerHTML = event;
        }

        // Asynchronously adjust the role attribute for better accessibility
        setTimeout(() => {
            this.calendarTable = document.querySelector('.fc-list-table');

            if (info.view.type === 'dayGridMonth' || info.view.type === 'dayGridWeek' ) {
                const tableContainer: HTMLTableElement = document.querySelector('.fc-dayGridMonth-view > table');

                if (tableContainer && !tableContainer.hasAttribute('aria-prev-hidden')) {
                    tableContainer.setAttribute('aria-prev-hidden', 'true');
                    groupRowsByDate(tableContainer, this.previousEventsBtn, this.viewMode);
                }

            }
            if (this.calendarTable && !this.calendarTable.hasAttribute('role')) {
                this.calendarTable.setAttribute('role', 'presentation');
                groupRowsByDate(this.calendarTable, this.previousEventsBtn, this.viewMode);
            }
        }, 0);
        // Return custom event container only for non-dayGridMonth views
        return { domNodes: [eventContainer] };
    }

    // Switch the calendar view based on the provided data and date
    public calendarView(data: CalendarViewType, date: Date) {
        this.viewMode = data;
        this.calendar.gotoDate(date);
        this.referenceMonth = moment(date).month();
        this.calendar.changeView(this.viewMode);
        this.changeToggleViewBtn(this.viewMode)
    }

    public toggleCalendarView(currentView: CalendarViewType): CalendarViewType {
        return toggleCalendarView(currentView)
    }
    public getCalendarView(filterState: FilterState) {
        const activeButton = document.querySelector('.calendar-toggle-view-btn.active') as HTMLButtonElement;
        const prevMode = activeButton?.getAttribute('aria-btn-view') || 'listMonth';
        const view = getCalendarView(filterState, this.getViewMode(), prevMode as CalendarViewType);
        return view;
    }

    // Update the event source with the provided array of events
    public updateEventSource(events: EventsObj) {
        this.events = events;
        this.calendar.removeAllEventSources();
        this.calendar.addEventSource(events.filtered);
    }

    // Trigger a rerender of the calendar
    public renderCalendar() {
        this.showLoading(false)
        this.calendar.render();
    }

    public showInfoMessage() {
        this.showMessage(false);
       
        setTimeout(() => {
            this.showNoEventsInfo()
        }, 100)
    }

    public getViewMode(): CalendarViewType {
        return this.viewMode;
    }

    /**
     * Toggles the visibility of a loading indicator in the calendar view.
     * If `isShow` is `true`, the function destroys the calendar, hides the "Previous Events" button,
     * and displays a loading message. If `isShow` is `false`, it hides the loading message.
     * @param {boolean} isShow - A boolean indicating whether to show or hide the loading indicator.
     */
    public showLoading(isShow: boolean): void {
        if (isShow) {
            // If showing loading indicator:
            // 1. Destroy the calendar.
            // 2. Hide the "Previous Events" button.
            // 3. Display a loading message.
            this.calendar.destroy();
            this.previousEventsBtn.style.visibility = 'hidden';
            this.showMessage(true, 'LOADING...');
        } else {
            // If hiding loading indicator:
            // Hide the loading message.
            this.showMessage(false);
        }
    }
    
    /**
    * Controls the display of a message indicating the absence of events in the calendar view.
    * If there are no events, it shows a message with details about the current view mode.
    * If there are events, it hides the message. Additionally, if the "Previous Events" button is visible
    * and set to hide previous events, it shows the message only when there are no current month events.
    */
    private showNoEventsInfo(): void {
        // Retrieve all elements with the class 'calendar-event-row'.
        let eventRows = Array.from(document.getElementsByClassName('calendar-event-row')) as HTMLElement[];
        if (this.getViewMode() === 'dayGridMonth' || this.getViewMode() === 'dayGridDay' || this.getViewMode() === 'dayGridWeek') {
            eventRows = Array.from(document.getElementsByClassName('fc-daygrid-event')) as HTMLElement[]
        }
        if (eventRows.length === 0) {
            // If no events:
            // - Show a message with details about the current view mode.
            // - Hide the "Previous Events" button.
            this.showMessage(true, this.getMessage(eventRows, this.getSelectedMonthEvents(), this.viewMode));
            this.previousEventsBtn.style.visibility = 'hidden';
        } else {
            // Filter elements to get those with the class 'prev-events'.
            const previousEvents = eventRows.filter(e => e.classList.contains('prev-events'));

            const isVisiblePreviousBtn: boolean = this.previousEventsBtn.style.visibility === 'visible';
            const isHidePreviousEvents: boolean = this.previousEventsBtn.innerHTML === 'PREVIOUS EVENTS';

            if (isVisiblePreviousBtn && isHidePreviousEvents) {
                // If the "Previous Events" button is visible and set to hide previous events:
                // - Show the message if there are no current month events.
                // - Hide the message otherwise.

                if (this.viewMode === 'listAllYears') {
                    if (eventRows.length === previousEvents.length) {
                        showPrevious(this.calendarTable, this.previousEventsBtn, this.viewMode);
                        this.previousEventsBtn.style.visibility = 'hidden'
                    } else {
                        this.showMessage(false);
                    }
                } else {
                    eventRows.length === previousEvents.length
                        ? this.showMessage(true, this.getMessage(eventRows, this.getSelectedMonthEvents(), this.viewMode))
                        : this.showMessage(false);
                }

            } else {
                // If the "Previous Events" button is not visible or set to show previous events, hide the message.
                this.showMessage(false);                    
            }
        }
    }

    /**
     * Controls the visibility and content of a message within a designated HTML element.
     * If `show` is `true`, it sets the specified text and makes the element visible.
     * If `show` is `false`, it clears the text and hides the element.
     * @param {boolean} show - A boolean indicating whether to show or hide the message.
     * @param {string} text - The text to be displayed when showing the message (default is an empty string).
     */
    public showMessage = (show: boolean, text: string = ''): void => {
        this.checkDayGridView(show)
        // Retrieve the designated HTML element where the message will be displayed.
        const pageHeadingNoEventsInfo = document.querySelector('#NoeventFoundMsg') as HTMLElement;

        const textMessage = pageHeadingNoEventsInfo.querySelector(':scope > h4') as HTMLElement;
        const emptyState = pageHeadingNoEventsInfo.querySelector('[data-empty-state]') as HTMLElement;
        const isEmptyState = show && emptyStateMessages.includes(text);

        // Set the inner HTML and display style based on the show parameter.
        // "No events" messages get the illustrated empty state instead of the text message.
        textMessage.innerHTML = show && !isEmptyState ? text : '';
        textMessage.style.display = isEmptyState ? 'none' : '';
        emptyState.querySelector('[data-empty-state-text]').textContent = isEmptyState ? text : '';
        (emptyState.querySelector('[data-empty-state-title]') as HTMLElement).style.display =
            checkBackSoonMessages.includes(text) ? '' : 'none';
        emptyState.style.display = isEmptyState ? 'block' : 'none';
        pageHeadingNoEventsInfo.style.display = show ? 'block' : 'none';
    };

    /**
     * Returns every loaded event (all calendars, before filters) that intersects the month of the calendar's current date.
     * `events.month` is not used here: the initial all-events fetch resets it to an empty array.
     */
    private getSelectedMonthEvents(): CalendarEvent[] {
        const selectedDate = moment(this.calendar.getDate());
        const monthStart = selectedDate.clone().startOf('month').valueOf();
        const monthEnd = selectedDate.clone().endOf('month').valueOf();

        return (this.events.all || []).filter(event => doesEventIntersectWithMonth(event, monthStart, monthEnd));
    }

    /**
     * Generates a message based on the presence or absence of events in the calendar view.
     * If there are no events, it returns a message indicating the absence of events.
     * If there are events, it returns a message encouraging the user to select a calendar from the Filter Calendars list.
     * The specific message is tailored based on the current view mode.
     * @param {HTMLElement[]} eventRows - An array of HTML elements representing calendar event rows.
     * @param {CalendarEvent[]} monthEvents - An array of calendar events for the current month.
     * @param {'listDay' | 'listMonth'} viewMode - The current view mode of the calendar ('listDay' or 'listMonth').
     * @returns {string} - The generated message based on the presence or absence of events and the view mode.
     */
    getMessage(eventRows: HTMLElement[], monthEvents: CalendarEvent[], viewMode: CalendarViewType): string {
        const selectedDate = moment(this.calendar.getDate());
        const monthStart = selectedDate.clone().startOf('month');
        const monthEnd = selectedDate.clone().endOf('month');
        const now = moment();
        // A month that is already over has nothing coming up, so it keeps the plain messages below.
        const isPastMonth = monthEnd.isBefore(now);

        if (!isPastMonth) {
            // Look for events from today, or from the selected day in a day view (the 1st in other views
            // of a future month), to the end of the month.
            const isDayView = viewMode === 'listDay' || viewMode === 'dayGridDay';
            const rangeStart = isDayView ? selectedDate.clone().startOf('day') : monthStart;
            const upcomingFrom = moment.max(now, rangeStart);
            const hasUpcomingEvents = monthEvents.some(event =>
                doesEventIntersectWithMonth(event, upcomingFrom.valueOf(), monthEnd.valueOf())
            );

            if (!hasUpcomingEvents) {
                return infoMessage.noEventsThisMonth;
            }
        }

        // Check if there are no events.
        if (monthEvents.length === 0) {
            return infoMessage.noEvents;
        }
        if (viewMode === 'listMonth' || viewMode === 'listAllYears' || viewMode === 'dayGridMonth' || viewMode === 'dayGridWeek') {
            // If the current view mode is 'listMonth':
            if (eventRows.length === 0) {
                // If there are no calendar event rows, encourage selecting a calendar.
                return infoMessage.selectCalendar;
            }

            // Extract unique calendar event IDs from previous events.
            const previousUniqIds: Set<number> = new Set(
                eventRows
                    .filter(row => row.classList.contains('prev-events'))
                    .map(row => Number(row.getAttribute('calendar-event-id')))
            );

            // Check if there are no current month events.
            return monthEvents.length - previousUniqIds.size === 0
                ? infoMessage.noEvents
                : infoMessage.selectCalendar;
        } else {
            // If the current view mode is 'listDay':
            const dayStart: Date = moment(this.calendar.getDate()).startOf('day').toDate();
            const dayEnd: Date = moment(this.calendar.getDate()).endOf('day').toDate();

            // Filter events that intersect with the selected day.
            const dayEvents = monthEvents.filter(event =>
                doesEventIntersectWithMonth(event, dayStart.getTime(), dayEnd.getTime())
            );

            // Check if there are no events for the selected day.
            if (dayEvents.length === 0) {
                return isPastMonth ? infoMessage.noEvents : infoMessage.noEventsThisDay;
            }
            return infoMessage.selectCalendar;
        }
    }
}